import React, { useState, useEffect, useMemo } from 'react';
import { subscribeToUserCollection, deleteUserDoc } from '../utils/userDataHelper';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/inventory-pages.css';
import useCurrencyTools from '../hooks/useCurrencyTools';

export default function MyPurchasesPage() {
  const [purchases, setPurchases] = useState([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const { preferredCurrency, formatMoney, convertAndFormat, convertMoney } = useCurrencyTools();

  useEffect(() => {
    const unsub = subscribeToUserCollection('purchases', setPurchases);
    return () => unsub();
  }, []);

  const handleDelete = async (id) => {
    const purchase = purchases.find(item => item.id === id);
    const linked = purchase?.inventoryApplied === true;
    if (!window.confirm(linked ? 'Delete this purchase and reverse its stock and weighted cost? This stops if the stock has been consumed or valuation cannot be reconciled.' : 'Delete this legacy purchase record? Its stock effect is unverified, so inventory will not change.')) return;
    try {
      await deleteUserDoc('purchases', id);
    } catch (err) {
      console.error('Error deleting purchase:', err);
      alert('Failed to delete purchase: ' + err.message);
    }
  };

  const filtered = useMemo(() => {
    let list = [...purchases];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        (p.ingredientName || '').toLowerCase().includes(q) ||
        (p.supplier || '').toLowerCase().includes(q) ||
        (p.invoiceNo || '').toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc': return (b.date || '').localeCompare(a.date || '');
        case 'date-asc': return (a.date || '').localeCompare(b.date || '');
        case 'cost-desc': return (b.totalCost || 0) - (a.totalCost || 0);
        case 'cost-asc': return (a.totalCost || 0) - (b.totalCost || 0);
        case 'name': return (a.ingredientName || '').localeCompare(b.ingredientName || '');
        default: return 0;
      }
    });
    return list;
  }, [purchases, search, sortBy]);

  const totalSpentConverted = purchases.reduce((sum, p) => {
    const origCurrency = p.currencyCode || 'INR';
    return sum + (p.totalCost ? convertMoney(p.totalCost, origCurrency, preferredCurrency) : 0);
  }, 0);
  const totalItems = purchases.length;
  const uniqueIngredients = new Set(purchases.map(p => p.ingredientName)).size;

  return (
    <>
      <div className="sl-page inventory-subpage">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Inventory', to: '/inventory' },
            { label: 'My Purchases' },
          ]} />
          <div className="pageheader-row">
            <div className="pageheading">My Purchases</div>
            <div className="pagedescription">Track all ingredient purchases and spending.</div>
          </div>
        </div>

        <div className="inventory-subpage-content borderleft">
          {/* Stats */}
          <section className="inv-card mypadding borderbottom">
            <div className="inv-stats-row">
            <div className="inv-stat-item">
              <span className="inv-stat-label">Total Spent</span>
              <span className="inv-stat-value">{formatMoney(totalSpentConverted, preferredCurrency, { maximumFractionDigits: 0 })}</span>
            </div>
              <div className="inv-stat-item">
                <span className="inv-stat-label">Purchases</span>
                <span className="inv-stat-value">{totalItems}</span>
              </div>
              <div className="inv-stat-item">
                <span className="inv-stat-label">Ingredients</span>
                <span className="inv-stat-value">{uniqueIngredients}</span>
              </div>
            </div>
          </section>

          {/* Toolbar */}
          <section className="inv-card mypadding borderbottom">
            <div className="inv-search">
              <input
                type="text"
                placeholder="Search by ingredient, supplier, or invoice..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="inv-btn inv-btn-secondary"
              >
                <option value="date-desc">Newest first</option>
                <option value="date-asc">Oldest first</option>
                <option value="cost-desc">Highest cost</option>
                <option value="cost-asc">Lowest cost</option>
                <option value="name">By ingredient</option>
              </select>
              <Link to="/inventory/purchase" className="inv-btn inv-btn-primary" style={{ textDecoration: 'none' }}>
                + Add Purchase
              </Link>
            </div>
          </section>

          {/* Purchase list */}
          <section className="inv-card mypaddingmoretop">
            {filtered.length === 0 ? (
              <div className="inv-empty">
                <p>{purchases.length === 0 ? 'No purchases recorded yet.' : 'No results match your search.'}</p>
                {purchases.length === 0 && (
                  <Link to="/inventory/purchase" className="inv-btn inv-btn-primary" style={{ marginTop: 16, textDecoration: 'none' }}>
                    Record First Purchase
                  </Link>
                )}
              </div>
            ) : (
              <div className="inv-list">
                <div className="inv-list-header inv-cols-5">
                  <div>Ingredient</div>
                  <div>Date</div>
                  <div>Qty</div>
                  <div>Cost</div>
                  <div></div>
                </div>
                {filtered.map(p => (
                  <div key={p.id} className="inv-list-row inv-cols-5">
                    <div data-label="Ingredient">
                      <strong>{p.ingredientName}</strong>
                      {p.supplier && <div style={{ fontSize: '0.85em', color: 'var(--sl-gray-500)' }}>{p.supplier}</div>}
                      <div style={{ fontSize: '0.8em', color: 'var(--sl-gray-500)' }}>{p.inventoryApplied === true ? 'Stock linked · deletion reverses stock' : 'Legacy record · stock effect unverified'}</div>
                      {p.invoiceNo && <div style={{ fontSize: '0.8em', color: 'var(--sl-gray-400)' }}>#{p.invoiceNo}</div>}
                    </div>
                    <div data-label="Date" className="bodytext">
                      {p.date ? new Date(p.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </div>
                    <div data-label="Quantity" className="bodytext">
                      {p.quantity} {p.unit}
                    </div>
                  <div data-label="Cost">
                      <strong>{convertAndFormat(p.totalCost || 0, p.currencyCode || 'INR', preferredCurrency, { maximumFractionDigits: 0 })}</strong>
                      {(p.currencyCode && p.currencyCode !== preferredCurrency) && (
                        <div style={{ fontSize: '0.8em', color: 'var(--sl-gray-500)' }}>
                          Original: {formatMoney(p.totalCost || 0, p.currencyCode || 'INR', { maximumFractionDigits: 0 })}
                        </div>
                      )}
                      <div style={{ fontSize: '0.8em', color: 'var(--sl-gray-500)' }}>
                        {convertAndFormat(p.costPerUnit || 0, p.currencyCode || 'INR', preferredCurrency, { maximumFractionDigits: 2 })}/{p.unit}
                        {(p.currencyCode && p.currencyCode !== preferredCurrency) && ` • Orig ${formatMoney(p.costPerUnit || 0, p.currencyCode || 'INR', { maximumFractionDigits: 2 })}/${p.unit}`}
                      </div>
                    </div>
                    <div>
                      <button
                        className="inv-btn inv-btn-danger inv-btn-sm"
                        onClick={() => handleDelete(p.id)}
                      >
                        {p.inventoryApplied === true ? 'Reverse & Delete' : 'Delete Record'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      <Footer />
      </div>
    </>
  );
}
